import SearchIcon from '@mui/icons-material/Search';
import { Box, Divider, IconButton, InputBase, Paper, type SxProps, type Theme, Typography } from '@mui/material';
import React from 'react';

interface SearchInputProps {
  placeholder: string;
  text: string;
  action: (search: string) => void;
  /** Shown at the end of the box while a search is typed; left out where the page counts the results itself. */
  count?: number;
  /** `plain` stands in a bar that is already framed, as a file manager's search does: no frame of its own. */
  variant?: 'framed' | 'plain';
  /** Laid over the box's own styles; its 400px width, where a toolbar needs it to give way. */
  sx?: SxProps<Theme>;
}

const SearchInput: React.FC<SearchInputProps> = ({
  text: searchTerm, action: setSearchTerm, count, placeholder, variant = 'framed', sx,
}) => {
  const isPlain = variant === 'plain';

  return (
    <Paper
      component="form"
      onSubmit={(e) => e.preventDefault()}
      sx={[isPlain ? plainStyles : { display: 'flex', alignItems: 'center', width: 400 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {isPlain
        ? <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
        : <IconButton sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>}
      {!isPlain && <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />}
      <InputBase
        sx={{ ml: isPlain ? 0 : 1, flex: 1 }}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {searchTerm && count !== undefined && (
        <>
          {!isPlain && <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '50px', height: '30px' }}>
            <Typography variant="body2">{count}</Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

const plainStyles = {
  display: 'flex',
  alignItems: 'center',
  width: 400,
  maxWidth: '100%',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  // The size of the toolbar's and the tree's words beside it.
  '& .MuiInputBase-root': {
    fontSize: 13.5,
  },
};

export default SearchInput;
